// مفتاح Claude للمستشار الذكي: يُحفظ مشفرًا في الخادم ولا يعود للمتصفح أبدًا.
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { GlassCard, Skeleton } from "@/v2/admin/ui";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";

const MODEL_AR = {
  "claude-opus-5-5": "Claude Opus 5.5 — أعلى دقة",
  "claude-sonnet-5-5": "Claude Sonnet 5.5 — متوازن",
  "claude-haiku-5-5": "Claude Haiku 5.5 — الأسرع والأوفر",
};

export default function AdvisorAISettings() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ["advisor-ai-settings"], queryFn: async () => (await api.get("/admin/advisor-ai/settings")).data });
  const [key, setKey] = useState("");
  const [model, setModel] = useState("");
  const [limit, setLimit] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (enabled = true) => {
    setBusy(true);
    try {
      const body = { api_key: key, model: model || data?.model, daily_limit: Number(limit || data?.daily_limit || 8), enabled };
      const res = await api.put("/admin/advisor-ai/settings", body, { timeout: 45000 });
      setKey(""); qc.setQueryData(["advisor-ai-settings"], res.data); qc.invalidateQueries({ queryKey: ["masar-advisor"] });
      toast.success(enabled ? "تم التحقق من المفتاح وتفعيل المستشار الذكي" : "أُوقف المستشار الذكي، ويعمل الآن بقواعد التخصص");
    } catch (e) { toast.error(e.response?.data?.detail || "تعذر الحفظ، أعد المحاولة"); }
    finally { setBusy(false); }
  };
  const removeKey = async () => {
    setBusy(true);
    try { qc.setQueryData(["advisor-ai-settings"], (await api.delete("/admin/advisor-ai/settings/key")).data); toast.success("حُذف المفتاح المحفوظ"); }
    catch (e) { toast.error(e.response?.data?.detail || "تعذر حذف المفتاح"); }
    finally { setBusy(false); }
  };

  if (isLoading) return <Skeleton className="h-64 max-w-3xl" />;
  if (error) return <GlassCard className="p-5 max-w-3xl"><button className="v2-btn" onClick={() => refetch()}>إعادة تحميل الإعدادات</button></GlassCard>;

  return (
    <GlassCard className="p-5 sm:p-6 max-w-3xl space-y-5" data-testid="advisor-ai-settings">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <h2 className="m-0 flex items-center gap-2"><Sparkles size={20} className="text-beige-ink" /> المستشار الذكي (Claude)</h2>
          <p className="text-sm text-ink-2 mt-1 mb-0">يختار لكل موظف في قطاع الخدمات المشتركة ما يحتاجه من شهادات وظيفته، ويستبعد ما يملكه أو يعادله.</p>
        </div>
        <span className={`state-chip ${data?.enabled ? "done" : "next"}`}><i />{data?.enabled ? "مفعّل" : "يعمل بقواعد التخصص"}</span>
      </div>

      {data?.configured && (
        <div className="v2-inset px-4 py-3 text-sm flex items-center gap-2 flex-wrap">
          <CheckCircle2 size={16} className="text-ok" />
          {data.source === "dashboard" ? <>مفتاح محفوظ ينتهي بـ <b dir="ltr">…{data.key_hint}</b></> : <>المفتاح مضبوط من إعدادات الخادم</>}
          {data.verified_at && <span className="text-ink-3">· آخر تحقق {new Date(data.verified_at).toLocaleDateString("ar-SA")}</span>}
        </div>
      )}
      {data?.needs_resave && <p className="text-sm text-danger m-0">تغيّر مفتاح تشفير الخادم، فألصق مفتاح Claude من جديد واحفظه.</p>}

      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save(true); }}>
        <label className="block">
          <span className="v2-label">مفتاح Claude API</span>
          <input id="advisor-api-key" className="v2-input" dir="ltr" type="password" autoComplete="new-password" spellCheck={false}
            placeholder={data?.configured ? "اتركه فارغًا للاحتفاظ بالمفتاح الحالي" : "sk-ant-…"} value={key} onChange={(e) => setKey(e.target.value)} disabled={busy} />
        </label>
        <div className="grid sm:grid-cols-[1fr_180px] gap-4">
          <label className="block">
            <span className="v2-label">النموذج</span>
            <select id="advisor-model" className="v2-input" value={model || data?.model} onChange={(e) => setModel(e.target.value)} disabled={busy}>
              {(data?.models || []).map((m) => <option key={m} value={m}>{MODEL_AR[m] || m}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="v2-label">حد الطلبات اليومي لكل موظف</span>
            <input id="advisor-limit" className="v2-input tabular-nums" type="number" min={1} max={50} value={limit || data?.daily_limit || 8} onChange={(e) => setLimit(e.target.value)} disabled={busy} />
          </label>
        </div>
        <p className="text-sm text-ink-2 leading-7 m-0">
          يُحفظ المفتاح مشفرًا في الخادم ولا يظهر بعد الحفظ. يُرسل لـ Claude المسمى والمرتبة والجدارات وأسماء الشهادات فقط، بلا اسم الموظف أو رقمه أو بريده. التكلفة على رصيد حساب Claude API.
        </p>
        <div className="flex gap-3 flex-wrap items-center">
          <button className="v2-btn" disabled={busy || (!data?.configured && !key.trim())} data-testid="advisor-ai-save">
            {busy && <Loader2 size={16} className="animate-spin" />} التحقق والحفظ والتفعيل
          </button>
          {data?.enabled && <button type="button" className="v2-btn-ghost" disabled={busy} onClick={() => save(false)}>إيقاف المستشار الذكي</button>}
          {data?.source === "dashboard" && <button type="button" className="v2-btn-danger" disabled={busy} onClick={removeKey}>حذف المفتاح</button>}
        </div>
        <p className="text-sm m-0">تحصل على المفتاح من <span dir="ltr" className="font-semibold select-all">console.anthropic.com</span> ← API Keys.</p>
      </form>
    </GlassCard>
  );
}
